<template>
  <base-drawer :show="show" title="Email" width="480px" @close="onClose">
    <template v-if="event">
      <drawer-section title="Email" icon="icon-email-85">
        <p class="text-white mb-1">{{ emailLabel(event) }}</p>
        <p class="text-white mb-1" v-if="event.subject_line">Subject: {{ event.subject_line }}</p>
        <small class="text-white d-block">{{ emailSourceLabel(event) }}</small>
      </drawer-section>

      <drawer-section title="Delivery" icon="icon-send">
        <div class="drawer-detail-row">
          <span class="text-white">Status</span>
          <span class="tag-chip text-white" :style="tagChipStyle(statusColor)">{{ statusLabel }}</span>
        </div>
        <div class="drawer-detail-row" v-if="!history.length">
          <span class="text-white">Sent</span>
          <span class="text-white">{{ formatWhen(event.timestamp) }}</span>
        </div>
        <template v-if="history.length">
          <div class="drawer-detail-row" v-for="h in history" :key="'H' + h.id">
            <span class="text-white">{{ eventLabel(h) }}</span>
            <span class="text-white">{{ formatWhen(h.timestamp) }}</span>
          </div>
        </template>
        <div class="drawer-detail-row" v-else-if="event.latest_event_at && event.latest_event !== 'Send'">
          <span class="text-white">{{ statusLabel }}</span>
          <span class="text-white">{{ formatWhen(event.latest_event_at) }}</span>
        </div>
        <small v-if="loadingHistory" class="d-block text-white">Loading history...</small>
        <div class="drawer-detail-row">
          <span class="text-white">To</span>
          <span class="text-white text-right">
            <router-link v-if="recipientName" class="text-white" :to="{ path: '/customers/details', query: { id: event.recipient_id } }">{{ recipientName }}</router-link>
            <small class="d-block text-white">{{ event.recipient_email }}</small>
          </span>
        </div>
        <div class="drawer-detail-row" v-if="event.transaction_id">
          <span class="text-white">Transaction</span>
          <router-link class="text-white" :to="{ path: '/purchases/transactions/details', query: { id: event.transaction_id } }">{{ event.transaction_id }}</router-link>
        </div>
      </drawer-section>
    </template>

    <template #footer>
      <base-button type="secondary" size="sm" @click="onClose">Close</base-button>
    </template>
  </base-drawer>
</template>

<script setup lang="ts">
// Read-only detail for one row of the email log. Opening it fetches the message's
// event timeline (every SES event for its message id) from get-email-log-for-group.
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { tagChipStyle } from '@/util/tagChip'
import { EMAIL_STATUS_LABELS, EMAIL_STATUS_COLORS, EMAIL_EVENT_LABELS, emailLabel, emailSourceLabel, eventToStatus } from './emailLogLabels'

const props = defineProps<{ show: boolean; event: any | null }>()
const emit = defineEmits<{ (e: 'close'): void }>()

const statusKey = computed(() => (props.event ? eventToStatus(props.event.latest_event) : 'sent'))
const statusLabel = computed(() => EMAIL_STATUS_LABELS[statusKey.value])
const statusColor = computed(() => EMAIL_STATUS_COLORS[statusKey.value])
const recipientName = computed(() =>
  props.event ? [props.event.recipient_first_name, props.event.recipient_last_name].filter(Boolean).join(' ') : '')

const history = ref<any[]>([])
const loadingHistory = ref(false)
const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id

function eventLabel(h: any) {
  const base = EMAIL_EVENT_LABELS[h.event_type] || h.event_type
  return h.event_type === 'Bounce' && h.bounce_type ? base + ' (' + String(h.bounce_type).toLowerCase() + ')' : base
}

// Every event of the message, oldest first; a legacy row without a message id has only its send
async function loadHistory() {
  history.value = []
  if (!props.event || !props.event.message_id) return
  loadingHistory.value = true
  try {
    const session = await Auth.currentSession()
    const config = { headers: { Authorization: (session as any).idToken.jwtToken } }
    const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/email-log?message_id=${encodeURIComponent(props.event.message_id)}`
    const response = await axios.get(url, config)
    history.value = response.data.history || []
  } catch (e) {
    history.value = []
  } finally {
    loadingHistory.value = false
  }
}

function formatWhen(value: string) {
  if (!value) return ''
  try {
    return formatGroupDate(value) + ' ' + formatGroupTime(value)
  } catch (e) {
    return String(value).slice(0, 16)
  }
}

function onClose() {
  emit('close')
}
function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape') onClose()
}
watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    loadHistory()
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})
</script>
